import React, { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { entitlementsApi } from 'modules/entitlements';

// Billing lives in the settings dialog; the URL already carries a query
// string, so extra params (e.g. topup=success) append with `&`.
const BILLING_URL = '/app?settings=billing';
const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2500;
const COIN_RETURN_DELAY_MS = 1800;

/** Re-checks the order with the provider (ATMOS) so a lost callback cannot strand a paid order. */
const syncOrder = async (orderId: string | null) => {
  if (!orderId) return;
  try {
    await entitlementsApi.SyncPaymentOrder(orderId);
  } catch {
    // Best effort: the webhook and the server janitor still activate the order.
  }
};

/** Activation happens server-side via the webhook; the hard navigation re-runs the profile fetch. */
const BillingReturn: React.FC = () => {
  const { t } = useTranslation();
  const attempts = useRef(0);
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const isCoinTopup = params?.get('kind') === 'coins';
  const orderId = params?.get('order') ?? null;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    if (isCoinTopup) {
      void syncOrder(orderId);
      timer = setTimeout(() => {
        window.location.href = `${BILLING_URL}&topup=success`;
      }, COIN_RETURN_DELAY_MS);
      return () => clearTimeout(timer);
    }

    const goToBilling = () => {
      window.location.href = BILLING_URL;
    };

    const poll = async () => {
      await syncOrder(orderId);
      if (cancelled) return;
      try {
        const { data } = await entitlementsApi.GetEntitlements();
        if (cancelled) return;
        if (data?.subscriptionStatus === 'active' || data?.subscriptionStatus === 'trialing') {
          goToBilling();
          return;
        }
      } catch {
        // transient — fall through to retry
      }
      if (cancelled) return;
      attempts.current += 1;
      if (attempts.current >= MAX_ATTEMPTS) {
        goToBilling();
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isCoinTopup, orderId]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
      <div>
        <p className="text-lg font-semibold text-foreground">
          {isCoinTopup
            ? t('adding_coins_to_wallet', 'Adding coins to your wallet')
            : t('activating_subscription')}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {isCoinTopup ? t('topup_return_hint', 'This will only take a moment.') : t('payment_return_hint')}
        </p>
      </div>
    </div>
  );
};

export default BillingReturn;
